<script setup lang="ts">
import { computed } from 'vue'
import { getStateFullName } from '~/utils/states'
import { buildCompanyUrl } from '~/utils/formatters'

const props = defineProps<{
  brandName: string
  section?: string
  location?: { city?: string | null, state?: string | null }
  role?: string | null
  permalink?: string
}>()

const companySlug = computed(() => {
  if (props.permalink) return props.permalink
  const route = useRoute()
  const params = route.params.permalink as string[]
  return params[0]
})

const formattedSection = computed(() => {
  return props.section
    ? props.section.charAt(0).toUpperCase() + props.section.slice(1)
    : ''
})

const formattedLocation = computed(() => {
  if (!props.location) return null
  if (props.location.city && props.location.state) {
    return `${props.location.city}, ${props.location.state}`
  }
  return props.location.state ? getStateFullName(props.location.state) : null
})

const locationUrl = computed(() => {
  if (!props.location || !props.section) return ''

  return buildCompanyUrl(
    companySlug.value,
    props.section as 'salaries' | 'reviews',
    {
      city: props.location.city || undefined,
      stateCode: props.location.state || undefined
    }
  )
})

const formattedRole = computed(() => {
  if (!props.role) return null
  return props.role.replace(/-/g, ' ')
})
</script>

<template>
  <nav>
    <ol class="flex flex-wrap items-center justify-end space-x-2 text-sm">
      <li>
        <NuxtLink
          to="/company"
          class="text-inherit hover:opacity-80"
        >
          Companies
        </NuxtLink>
      </li>
      <li class="flex items-center">
        <span class="mx-1 opacity-70">/</span>
        <NuxtLink
          :to="`/company/${companySlug}`"
          class="text-inherit hover:opacity-80"
        >
          {{ brandName }}
        </NuxtLink>
      </li>
      <template v-if="section && section !== 'overview'">
        <li class="flex items-center">
          <span class="mx-1 opacity-70">/</span>
          <NuxtLink
            :to="`/company/${companySlug}/${section}`"
            class="text-inherit hover:opacity-80"
          >
            {{ formattedSection }}
          </NuxtLink>
        </li>
      </template>
      <template v-if="formattedLocation">
        <li class="flex items-center">
          <span class="mx-1 opacity-70">/</span>
          <NuxtLink
            v-if="formattedRole"
            :to="locationUrl"
            class="text-inherit hover:opacity-80"
          >
            {{ formattedLocation }}
          </NuxtLink>
          <span
            v-else
            class="opacity-90"
          >{{ formattedLocation }}</span>
        </li>
      </template>
      <template v-if="formattedRole">
        <li class="flex items-center">
          <span class="mx-1 opacity-70">/</span>
          <span class="opacity-90">{{ formattedRole }}</span>
        </li>
      </template>
    </ol>
  </nav>
</template>
